import { Link, usePage } from "@inertiajs/react";
import type { ReactNode } from "react";

import Carousel from "@/components/Carousel";
import { CalendarIcon, ClockIcon, PinIcon } from "@/components/icons";
import StudentCourseCard, {
  type MyEnrollment,
  type MyWaitList,
} from "@/components/StudentCourseCard";
import { usePermissions } from "@/lib/permissions";
import { courses as courseRoutes, editProfileUser } from "@/routes";
import type { Course, SharedProps, User } from "@/types";

type Props = {
  data: User;
  pastCourses: Course[];
  futureCourses: Course[];
  myEnrollments: MyEnrollment[];
  myWaitLists: MyWaitList[];
};

function formatBirthDate(date: string | null): string {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

/** Icon + value, no caption above — used for the fields that already read
 *  clearly from their icon alone (email, phone, address). */
function IconRow({ icon, value }: { icon: ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-ink/50">{icon}</span>
      <span className="font-sans text-base text-ink">{value || "-"}</span>
    </div>
  );
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.5 6l8.5 7 8.5-7"
      />
    </svg>
  );
}

/** Same icon as the header's profile menu, for the RUT row. */
function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 19.5a7.5 7.5 0 0 1 15 0v.75H4.5v-.75Z"
      />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 5.25c0-.83.67-1.5 1.5-1.5h2.038c.7 0 1.303.48 1.462 1.161l.86 3.66a1.5 1.5 0 0 1-.44 1.44l-1.35 1.28a12.02 12.02 0 0 0 5.39 5.39l1.28-1.35a1.5 1.5 0 0 1 1.44-.44l3.66.86c.681.16 1.161.762 1.161 1.462v2.038c0 .83-.67 1.5-1.5 1.5h-1.5C9.716 20.75 3.25 14.284 3.25 6.75v-1.5Z"
      />
    </svg>
  );
}

function CourseCarousel({
  title,
  courses,
  myEnrollments,
  myWaitLists,
  variant,
  linkToCourse = false,
}: {
  /** Staff viewing someone else's profile: cards open the course detail. */
  linkToCourse?: boolean;
  title: string;
  courses: Course[];
  myEnrollments: MyEnrollment[];
  myWaitLists: MyWaitList[];
  /** "cancel" (future courses) → live confirm/cancel actions; "past" → a
   *  read-only status label instead, since those courses already happened. */
  variant: "cancel" | "past";
}) {
  if (courses.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="mb-5 font-sans text-xl font-semibold text-ink">{title}</h2>
      <Carousel peek={false}>
        {courses.map((course) => (
          <StudentCourseCard
            key={course.id}
            course={course}
            variant={variant}
            href={linkToCourse ? courseRoutes.show(course.id).url : undefined}
            enrollment={myEnrollments.find((e) => e.course_id === course.id)}
            waitList={myWaitLists.find((w) => w.course_id === course.id)}
          />
        ))}
      </Carousel>
    </section>
  );
}

export default function UsersProfile({
  data: user,
  pastCourses,
  futureCourses,
  myEnrollments,
  myWaitLists,
}: Props) {
  const { currentUser } = usePage<SharedProps>().props;
  const { canViewUserProfiles } = usePermissions();
  const isOwnProfile = currentUser?.id === user.id;
  const linkToCourse = canViewUserProfiles && !isOwnProfile;
  const showLastSignIn = currentUser?.is_admin && !isOwnProfile;
  const address = [user.address, user.commune, user.city]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="mx-auto w-[90%] max-w-6xl py-10">
      <h1 className="mb-6 font-sans text-3xl font-semibold text-ink">
        {isOwnProfile ? `¡Hola ${user.first_name}!` : "Perfil"}
      </h1>

      <div className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="font-sans text-xl font-semibold text-ink">
              {(user.full_name as string | null) ?? ""}
            </h2>
            {showLastSignIn && (
              <span className="flex items-center gap-1 font-sans text-sm text-ink/50">
                <ClockIcon className="h-4 w-4" />
                Último acceso:{" "}
                {(user.last_sign_in_display as string | null) ?? "-"}
              </span>
            )}
          </div>
          {isOwnProfile && (
            <Link
              href={editProfileUser(user.id).url}
              className="w-fit font-sans text-sm text-steel underline underline-offset-2 hover:text-steel/80"
            >
              Editar perfil
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <IconRow
            icon={<UserIcon className="h-4 w-4" />}
            value={(user.rut_formatted as string | null) ?? ""}
          />
          <IconRow
            icon={<EmailIcon className="h-4 w-4" />}
            value={user.email ?? ""}
          />
          <IconRow
            icon={<PhoneIcon className="h-4 w-4" />}
            value={user.phone_number ?? ""}
          />
          <IconRow
            icon={<CalendarIcon className="h-4 w-4" />}
            value={formatBirthDate(user.birth_date)}
          />
          <IconRow icon={<PinIcon className="h-4 w-4" />} value={address} />
        </div>
      </div>

      <CourseCarousel
        title="Cursos futuros"
        courses={futureCourses}
        myEnrollments={myEnrollments}
        myWaitLists={myWaitLists}
        variant="cancel"
        linkToCourse={linkToCourse}
      />

      <CourseCarousel
        title="Cursos pasados"
        courses={pastCourses}
        myEnrollments={myEnrollments}
        myWaitLists={myWaitLists}
        variant="past"
      />
    </div>
  );
}
